<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import DriveAPI from 'dashboard/api/drive';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    required: true,
  },
  accept: {
    type: String,
    default: 'image/*',
  },
  previewSize: {
    type: String,
    default: 'md', // sm, md, lg
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const fileInput = ref(null);
const uploading = ref(false);
const isDragging = ref(false);

const sizeClasses = {
  sm: 'w-20 h-20',
  md: 'w-32 h-32',
  lg: 'w-48 h-48',
};

const iconSizes = {
  sm: 'size-6',
  md: 'size-8',
  lg: 'size-10',
};

const hasImage = computed(() => !!props.modelValue);

const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleDragOver = e => {
  e.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = () => {
  isDragging.value = false;
};

const uploadFile = async file => {
  uploading.value = true;

  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('drive_file[name]', file.name);
    formData.append('drive_file[folder_path]', '/website/branding');

    const response = await DriveAPI.create(formData);

    // La réponse peut avoir file_url, share_url ou url
    const fileUrl =
      response.data?.file_url || response.data?.share_url || response.data?.url;

    if (!fileUrl) {
      console.error('Upload response:', response.data);
      throw new Error('No file URL in response');
    }

    emit('update:modelValue', fileUrl);

    useAlert(t('DRIVE.API.SUCCESS.FILE_UPLOADED', { name: file.name }));
  } catch (error) {
    console.error('Image upload error:', error);
    const errorMessage =
      error.response?.data?.error ||
      error.message ||
      t('DRIVE.API.ERROR.UPLOAD_FAILED', { name: file.name });
    useAlert(errorMessage);
  } finally {
    uploading.value = false;
  }
};

const handleDrop = async e => {
  e.preventDefault();
  isDragging.value = false;

  const file = e.dataTransfer.files[0];
  if (file) {
    await uploadFile(file);
  }
};

const handleFileSelect = async event => {
  const file = event.target.files[0];
  if (file) {
    await uploadFile(file);
  }
  event.target.value = '';
};

const removeImage = () => {
  emit('update:modelValue', '');
};
</script>

<template>
  <div>
    <label class="block text-sm font-medium text-n-slate-12 mb-2">
      {{ label }}
    </label>

    <!-- Zone d'upload -->
    <div
      v-if="!hasImage"
      class="border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all"
      :class="[
        sizeClasses[previewSize],
        isDragging
          ? 'border-n-blue-9 bg-n-blue-2 scale-[0.98]'
          : 'border-n-weak hover:border-n-blue-6 hover:bg-n-blue-2/30',
      ]"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
      @click="triggerFileInput"
    >
      <div v-if="uploading" class="flex flex-col items-center gap-1">
        <Spinner />
        <span class="text-xs text-n-slate-11">{{
          t('WEBSITE.UPLOADER.UPLOADING')
        }}</span>
      </div>
      <div v-else class="flex flex-col items-center gap-1.5 p-3">
        <span
          class="i-lucide-image-plus text-n-slate-10"
          :class="iconSizes[previewSize]"
        />
        <span class="text-xs text-center text-n-slate-11 leading-tight">
          {{
            previewSize === 'sm'
              ? t('WEBSITE.UPLOADER.CLICK')
              : t('WEBSITE.UPLOADER.CLICK_OR_DROP')
          }}
        </span>
      </div>
    </div>

    <!-- Aperçu de l'image -->
    <div v-else class="relative group" :class="sizeClasses[previewSize]">
      <img
        :src="modelValue"
        :alt="label"
        class="w-full h-full object-contain rounded-lg border-2 border-n-weak"
      />
      <div
        class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2"
      >
        <button
          class="p-2 bg-white/90 dark:bg-n-slate-1/90 rounded-lg hover:bg-n-blue-2 transition-all"
          :title="t('WEBSITE.UPLOADER.REPLACE')"
          @click="triggerFileInput"
        >
          <span class="i-lucide-refresh-cw size-4 text-n-slate-12" />
        </button>
        <button
          class="p-2 bg-white/90 dark:bg-n-slate-1/90 rounded-lg hover:bg-n-ruby-2 transition-all"
          :title="t('WEBSITE.UPLOADER.DELETE')"
          @click="removeImage"
        >
          <span class="i-lucide-trash-2 size-4 text-n-slate-12" />
        </button>
      </div>
    </div>

    <!-- Input file caché -->
    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      class="hidden"
      @change="handleFileSelect"
    />
  </div>
</template>
